import { useState, useEffect, useMemo } from 'react';
import { IconUserSearch, IconCheck, IconX } from '@tabler/icons-react';
import sohozJobCustomerService from '../../services/sohozJobCustomerService';

// ── Map a saved Sohoz Job customer → the compact shape the invoice/ledger use.
// Kept identical to InvoicePage's mapping so a customer picked here fills an invoice
// exactly the same way (district/upazila are geo IDs, so only free-text parts are used).
export const custName = (c) => c?.applicant_name_bn || c?.applicant_name_en || '';
export const custAddr = (c) => [c?.present_village_road_house, c?.present_post_office].filter(Boolean).join(', ');
export const mapCustomer = (c) => ({ id: c?.id ?? null, name: custName(c), phone: c?.mobile_number || '', address: custAddr(c) });

/**
 * Searchable customer picker (Sohoz Job customers).
 *
 * Controlled by `value` (the name string). Typing calls `onChange(name)` — the
 * caller should clear any linked id then. Picking a suggestion calls
 * `onSelect({ id, name, phone, address })`. A green check shows while a customer
 * is linked (`linkedId` set), otherwise a search icon.
 */
export default function CustomerPicker({
    value = '',
    linkedId = null,
    onChange,
    onSelect,
    onClear,
    placeholder,
    inputClassName = '',
    disabled = false,
    maxLength,
}) {
    const [customers, setCustomers] = useState([]);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        let cancelled = false;
        sohozJobCustomerService.list()
            .then((res) => { if (!cancelled) setCustomers(res.data || []); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    const suggestions = useMemo(() => {
        const q = (value || '').trim().toLowerCase();
        const base = q
            ? customers.filter((c) =>
                custName(c).toLowerCase().includes(q) ||
                (c.applicant_name_en || '').toLowerCase().includes(q) ||
                (c.mobile_number || '').includes(q))
            : customers;
        return base.slice(0, 8);
    }, [customers, value]);

    return (
        <div className="relative">
            <div className="relative">
                <input
                    type="text"
                    value={value}
                    disabled={disabled}
                    maxLength={maxLength}
                    placeholder={placeholder}
                    onChange={(e) => { onChange?.(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    onClick={() => setOpen(true)}
                    onBlur={() => setTimeout(() => setOpen(false), 150)}
                    className={inputClassName}
                />
                {value && onClear ? (
                    // A green check marks a linked customer; the × clears it (name + id
                    // + phone + address) in a single click.
                    <>
                        {linkedId != null && (
                            <IconCheck size={15} className="absolute right-9 top-1/2 -translate-y-1/2 text-emerald-500" />
                        )}
                        <button
                            type="button"
                            tabIndex={-1}
                            onMouseDown={(e) => { e.preventDefault(); onClear(); setOpen(false); }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-md text-zinc-400 hover:text-red-500"
                        >
                            <IconX size={14} />
                        </button>
                    </>
                ) : linkedId != null ? (
                    <IconCheck size={15} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-emerald-500" />
                ) : (
                    <IconUserSearch size={15} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                )}
            </div>
            {open && suggestions.length > 0 && (
                <div className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 shadow-lg">
                    {suggestions.map((c) => (
                        <button
                            key={c.id}
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); onSelect?.(mapCustomer(c)); setOpen(false); }}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-zinc-50 dark:hover:bg-zinc-700/50 transition-colors"
                        >
                            <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-primary/10 text-primary text-[11px] font-bold">
                                {custName(c).charAt(0) || '?'}
                            </span>
                            <span className="min-w-0">
                                <span className="block text-sm text-zinc-800 dark:text-zinc-100 truncate">{custName(c)}</span>
                                <span className="block text-[11px] text-zinc-400 dark:text-zinc-500">{c.mobile_number}</span>
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
